import { TextButton, Typography } from "matsuri-ui"
import { css } from "@emotion/react"
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined"
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined"
import {
    TransportationExpense,
    displayExpenseDate,
    displayExpenseTimestamp,
    isExpenseEditable
} from "@/model/transportationExpense"
import { DateString } from "@/utils/dateString"
import { showJpYen } from "@/utils/showJpYen"
import { theme } from "@/theme"
import { useTranslation } from "@/hooks/useTranslation"

export const TransportationExpenseCard = ({
    expense,
    today,
    onEdit
}: {
    expense: TransportationExpense
    today: DateString
    onEdit: (expense: TransportationExpense) => void
}) => {
    const { t } = useTranslation()

    const editable = isExpenseEditable(expense.expenseDate, today)

    return (
        <div
            css={css`
                display: grid;
                gap: 10px;
                padding: 16px;
                background-color: white;
                border: 1px solid ${theme.palette.gray[300]};
                border-radius: 12px;
            `}
        >
            <div
                css={css`
                    display: flex;
                    gap: 12px;
                    align-items: flex-start;
                    justify-content: space-between;
                `}
            >
                <div
                    css={css`
                        display: grid;
                        gap: 2px;
                    `}
                >
                    <Typography
                        variant="caption"
                        css={css`
                            color: ${theme.palette.gray[500]};
                        `}
                    >
                        {t("清掃日")}
                    </Typography>
                    <Typography
                        variant="h4"
                        css={css`
                            font-weight: bold;
                        `}
                    >
                        {displayExpenseDate(expense.expenseDate)}
                    </Typography>
                </div>
                <Typography
                    variant="h3"
                    css={css`
                        font-weight: bold;
                        color: ${theme.palette.intentions.primary.main};
                        white-space: nowrap;
                    `}
                >
                    {showJpYen(expense.amount)}
                </Typography>
            </div>

            <div
                css={css`
                    display: flex;
                    gap: 6px;
                    align-items: center;
                `}
            >
                <RouteOutlinedIcon
                    css={css`
                        flex-shrink: 0;
                        font-size: 18px;
                        color: ${theme.palette.gray[500]};
                    `}
                />
                <Typography
                    variant="body2"
                    css={css`
                        color: ${theme.palette.gray[700]};
                        word-break: break-all;
                    `}
                >
                    {expense.routeDescription}
                </Typography>
            </div>

            <div
                css={css`
                    display: flex;
                    gap: 12px;
                    align-items: flex-start;
                    justify-content: space-between;
                    padding-top: 8px;
                    border-top: 1px solid ${theme.palette.gray[200]};
                `}
            >
                <div
                    css={css`
                        display: flex;
                        gap: 4px;
                        align-items: flex-start;
                    `}
                >
                    <AccessTimeOutlinedIcon
                        css={css`
                            flex-shrink: 0;
                            margin-top: 2px;
                            font-size: 14px;
                            color: ${theme.palette.gray[400]};
                        `}
                    />
                    <div
                        css={css`
                            display: grid;
                            gap: 2px;
                        `}
                    >
                        <Typography
                            variant="caption"
                            css={css`
                                color: ${theme.palette.gray[500]};
                            `}
                        >
                            {`${t("申請")}: ${displayExpenseTimestamp(expense.createdAt)}`}
                        </Typography>
                        {expense.updatedAt !== null && (
                            <Typography
                                variant="caption"
                                css={css`
                                    color: ${theme.palette.gray[500]};
                                `}
                            >
                                {`${t("修正")}: ${displayExpenseTimestamp(expense.updatedAt)}`}
                            </Typography>
                        )}
                    </div>
                </div>

                {editable && (
                    <TextButton
                        color="primary"
                        css={css`
                            flex-shrink: 0;
                        `}
                        onClick={() => onEdit(expense)}
                    >
                        {t("修正する")}
                    </TextButton>
                )}
            </div>
        </div>
    )
}
